import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export interface WsMessage {
  type: string;
  data: any;
}

interface WsState {
  messages: WsMessage[];
  connected: boolean;
  latest: Record<string, any>;
}

const WsContext = createContext<WsState>({
  messages: [],
  connected: false,
  latest: {},
});

// One WebSocket per tab, shared through context. Mounting one connection
// per useWebSocket() call previously opened 3 parallel sockets per page
// (Layout + page + ticker), which starved the browser's HTTP/1.1
// per-host connection budget during page load.
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<WsMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [latest, setLatest] = useState<Record<string, any>>({});

  useEffect(() => {
    let ws: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
      ws.onopen = () => setConnected(true);
      ws.onclose = (ev) => {
        setConnected(false);
        if (disposed) return;
        // 4401 = session rejected by /ws auth check. Stop retrying:
        // the next API call gets a 401 and routes to login instead.
        if (ev.code === 4401) return;
        retryTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws?.close();
      ws.onmessage = (event) => {
        try {
          const msg: WsMessage = JSON.parse(event.data);
          setLatest((prev) => ({ ...prev, [msg.type]: msg.data }));
          setMessages((prev) => [...prev.slice(-99), msg]);
        } catch {
          // ignore malformed messages
        }
      };
    };

    connect();
    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    };
  }, []);

  return (
    <WsContext.Provider value={{ messages, connected, latest }}>
      {children}
    </WsContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WsContext);
}
